import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Wallet, X, Zap } from 'lucide-react';

/**
 * Celebration modal shown after a successful Stripe top-up.
 * CSS-only confetti via 60 absolutely-positioned animated particles.
 * Auto-dismisses after 5s, or user can close early.
 */

const MESSAGES = [
  "You're unstoppable. Go build.",
  'Time to deploy some agents.',
  "Fuel's loaded. Let's make magic.",
  'Your sub-accounts thank you.',
  "That's what commitment looks like.",
  'Ready when you are.',
  'One more pro move logged.',
  "Let's ship something great.",
];

const COLORS = ['#14b8a6', '#22d3ee', '#fbbf24', '#f59e0b', '#a78bfa', '#f472b6', '#34d399'];

function ConfettiBurst({ count = 70 }) {
  const pieces = useMemo(() => Array.from({ length: count }).map((_, i) => {
    const angle = Math.random() * Math.PI * 2;
    const distance = 260 + Math.random() * 340;
    return {
      id: i,
      x: Math.cos(angle) * distance,
      y: Math.sin(angle) * distance - 60,
      rot: Math.random() * 720 - 360,
      scale: 0.6 + Math.random() * 0.9,
      color: COLORS[i % COLORS.length],
      delay: Math.random() * 0.15,
      width: 6 + Math.random() * 4,
      height: 10 + Math.random() * 6,
      isRound: Math.random() > 0.7,
    };
  }), [count]);

  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      {pieces.map((p) => (
        <motion.div
          key={p.id}
          initial={{ x: 0, y: 0, rotate: 0, opacity: 1, scale: p.scale }}
          animate={{
            x: p.x,
            y: [p.y, p.y + 180],
            rotate: p.rot,
            opacity: [1, 1, 0],
            scale: p.scale,
          }}
          transition={{
            duration: 2.6,
            delay: p.delay,
            ease: [0.2, 0.8, 0.2, 1],
            y: { duration: 2.6, delay: p.delay, ease: 'easeIn' },
          }}
          className="absolute top-1/2 left-1/2"
          style={{
            width: p.width,
            height: p.isRound ? p.width : p.height,
            background: p.color,
            borderRadius: p.isRound ? '999px' : '1px',
            boxShadow: `0 0 8px ${p.color}80`,
          }}
        />
      ))}
    </div>
  );
}

export default function TopUpCelebration({ amount, onClose, isFirstTopup = false }) {
  const [visible, setVisible] = useState(true);
  const message = useMemo(() => {
    if (isFirstTopup) return "Welcome aboard. Your first top-up is live.";
    return MESSAGES[Math.floor(Math.random() * MESSAGES.length)];
  }, [isFirstTopup]);

  useEffect(() => {
    const t = setTimeout(() => handleClose(), isFirstTopup ? 7000 : 5000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(() => onClose?.(), 350);
  };

  const formattedAmount = typeof amount === 'number' && !isNaN(amount)
    ? `$${amount.toLocaleString('en-US', { minimumFractionDigits: amount % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 })}`
    : null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[130] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md"
          data-testid="topup-celebration"
          onClick={handleClose}
        >
          <motion.div
            initial={{ scale: 0.8, y: 30, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.92, opacity: 0, y: -20 }}
            transition={{ type: 'spring', damping: 18, stiffness: 260 }}
            className={`relative w-full ${isFirstTopup ? 'max-w-lg p-12' : 'max-w-md p-10'} rounded-3xl border border-white/[0.08] bg-gradient-to-b from-[#12141a] via-[#0d0f13] to-[#0a0b0f] shadow-[0_40px_80px_-20px_rgba(0,0,0,0.95)]`}
            onClick={(e) => e.stopPropagation()}
            data-testid={isFirstTopup ? 'topup-celebration-first' : 'topup-celebration-default'}
          >
            {/* Confetti burst — denser for first top-up */}
            <ConfettiBurst count={isFirstTopup ? 120 : 70} />

            {/* Ambient glow */}
            <div aria-hidden className="absolute -top-32 left-1/2 -translate-x-1/2 w-80 h-80 rounded-full opacity-30 blur-3xl"
                 style={{ background: 'radial-gradient(circle, #fbbf24 0%, transparent 70%)' }} />

            <button
              onClick={handleClose}
              className="absolute top-3 right-3 p-1.5 rounded-lg text-slate-500 hover:text-white hover:bg-white/5 z-10"
              aria-label="Close"
              data-testid="topup-celebration-close"
            >
              <X size={16} />
            </button>

            {/* Content */}
            <div className="relative text-center">
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: 'spring', damping: 12, stiffness: 220, delay: 0.1 }}
                className="mx-auto w-20 h-20 rounded-[26px] flex items-center justify-center mb-5 shadow-[0_20px_40px_-10px_rgba(251,191,36,0.6)]"
                style={{ background: 'linear-gradient(135deg,#fbbf24,#f59e0b)' }}
              >
                <Zap className="text-[#0a0b0f]" size={36} fill="#0a0b0f" />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="mb-2"
              >
                {isFirstTopup ? (
                  <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-amber-400/20 to-yellow-300/20 border border-amber-400/40 text-[11px] uppercase tracking-[0.22em] font-extrabold text-amber-200">
                    <Sparkles size={12} />
                    First Top-up · Welcome Aboard
                  </div>
                ) : (
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-400/15 border border-emerald-400/30 text-[10px] uppercase tracking-[0.22em] font-extrabold text-emerald-300">
                    <Sparkles size={10} />
                    Balance Added
                  </div>
                )}
              </motion.div>

              {formattedAmount && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.7 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ type: 'spring', damping: 14, stiffness: 240, delay: 0.2 }}
                  className="mb-1"
                >
                  <div className="relative inline-block">
                    <span className="text-6xl sm:text-7xl font-bold text-white tracking-tight leading-none"
                          style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {formattedAmount}
                    </span>
                    <motion.span
                      initial={{ opacity: 0, scale: 0.5, x: 30, rotate: 20 }}
                      animate={{ opacity: 1, scale: 1, x: 0, rotate: 0 }}
                      transition={{ delay: 0.35, type: 'spring', damping: 12 }}
                      className="absolute -top-2 -right-8 text-3xl"
                      aria-hidden
                    >
                      ✨
                    </motion.span>
                  </div>
                </motion.div>
              )}

              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
                className="text-base text-slate-300 mt-3 mb-5"
              >
                {message}
              </motion.p>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="inline-flex items-center gap-2 text-xs text-slate-500 border-t border-white/[0.06] pt-4"
              >
                <Wallet size={12} />
                <span>Balance updated · Ready to run agents</span>
              </motion.div>

              {/* Auto-dismiss progress bar */}
              <motion.div
                aria-hidden
                initial={{ width: '100%' }}
                animate={{ width: '0%' }}
                transition={{ duration: isFirstTopup ? 7 : 5, ease: 'linear' }}
                className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-teal-400 via-cyan-400 to-emerald-400 rounded-b-3xl origin-left"
              />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
